import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {WhatsNewBanner} from 'shells/whats-new/whats-new-banner';

import type {OpenableModalId} from '../openable-modals/openable-modals';
import {openModal} from '../openable-modals/openable-modals-actions';

import type {LearnMoreConfig, OnDismissConfig} from './feedback-surveys';

export interface BannerProps {
    message: string;
    feedbackLabel?: string;
    onOpenSurvey: () => void;
    onHidden: () => void;
    animate: boolean;
    lessPadding: boolean;
}

interface Props extends BannerProps {
    learnMoreConfig?: LearnMoreConfig;
    onDismissConfig?: OnDismissConfig;
}

// Using custom handling here rather than the shared useOpenableModal hook
// because of the nullability/variability of the use cases here in which certain
// modal actions are optional.
function useFeedbackBannerOptionalModalOpen(modalId?: OpenableModalId): () => void {
    const dispatch = useDispatch();

    return useCallback(() => {
        if (modalId) {
            dispatch(openModal(modalId));
        }
    }, [dispatch, modalId]);
}

/*
 * Wraps WhatsNewBanner actions with openable modal logic.
 */
export function FeedbackBannerModalWrapper(props: Props) {
    const {learnMoreConfig, onDismissConfig, ...bannerProps} = props;
    const onHidden = bannerProps.onHidden;

    const onOpenLearnMore = useFeedbackBannerOptionalModalOpen(learnMoreConfig?.openableModalId);
    const onOpenDismiss = useFeedbackBannerOptionalModalOpen(onDismissConfig?.openableModalId);

    const handleLearnMoreClick = useCallback(() => {
        if (learnMoreConfig) {
            onOpenLearnMore();
        }
    }, [learnMoreConfig, onOpenLearnMore]);

    const handleDismissClick = useCallback(() => {
        if (onHidden) {
            onHidden();
        }

        if (onDismissConfig) {
            onOpenDismiss();
        }
    }, [onHidden, onDismissConfig, onOpenDismiss]);

    const bannerPropsWithDismiss = {
        ...bannerProps,
        onHidden: handleDismissClick,
    };

    return (
        <WhatsNewBanner
            {...bannerPropsWithDismiss}
            primaryLabel={learnMoreConfig?.label || 'Learn more'}
            onPrimaryClick={learnMoreConfig ? handleLearnMoreClick : undefined}
        />
    );
}
